import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration, SeatBackStyles, EmbroideryOptions } from '../../../data/configuration.js';
import { CameraController, CameraViews } from '../../../camera-controller.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import { getFilePathForLeatherStyleId } from './leather-view.tsx';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import { Utilities } from '../../../utilities/utilities.js';
import ViewContentRows from '../../ui/view-content-rows.tsx';
import { WingsLogo, CustomLogo } from '../../ui/headrest-embroidery-icons.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SeatsViewProps {
	interiorConfigurator: InteriorConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
}

interface SeatsViewContextType {
	selectedPleatingItemId: string;
	handlePleatingItemClicked: (id: string) => void;
	selectedEmbroideryItemId: string;
	handleEmbroideryItemClicked: (id: string) => void;
	interiorConfigurator: InteriorConfigurator;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function SeatsView(props: SeatsViewProps) {

	// initial values.
	const initialPleatingItemId = Configuration.seatPleatingOption;
	const initialPleatingItemName = () => getItemName(initialPleatingItemId);
	const initialEmbroideryItemId = Configuration.headrestEmbroideryOption;
	const initialEmbroideryItemName = () => getEmbroideryItemName(initialEmbroideryItemId);
	
	// states.
	const [selectedPleatingItemId, setSelectedPleatingItemId] = useState(initialPleatingItemId);
	const [selectedPleatingItemName, setSelectedPleatingItemName] = useState(initialPleatingItemName);
	const [selectedEmbroideryItemId, setSelectedEmbroideryItemId] = useState(initialEmbroideryItemId);
	const [selectedEmbroideryItemName, setSelectedEmbroideryItemName] = useState(initialEmbroideryItemName);
	
	// handlers.

	const handlePleatingItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedPleatingItemId(id);
		setSelectedPleatingItemName(getItemName(id));

		// update the configuration.
		Configuration.seatPleatingOption = id;
		Configuration.updateCosts();

		// select the seat pleating style on the car.
		props.interiorConfigurator.selectSeatPleatingStyle(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.SeatsView);
	};

	const handleEmbroideryItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedEmbroideryItemId(id);
		setSelectedEmbroideryItemName(getEmbroideryItemName(id));

		// update the configuration.
		Configuration.headrestEmbroideryOption = id;
		Configuration.updateCosts();

		// select the embroidery option on the car.
		props.interiorConfigurator.selectEmbroideryOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.HeadrestView);
	};

	// return the HTML.
	return (
		<SeatsViewContext.Provider value={{ selectedPleatingItemId, handlePleatingItemClicked, selectedEmbroideryItemId, handleEmbroideryItemClicked, interiorConfigurator: props.interiorConfigurator }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-seats">
							SEATS
						</h2>
					</div>
					
					<SectionPanel
						subHeading={'PLEATING'}
						modalContent={CloudData.getModalContent(selectedPleatingItemId, CloudData.seatPleatingOptions)}
					>
						<SelectedSectionItem
							itemName={selectedPleatingItemName}
							sectionPrice={CloudData.getPrice(selectedPleatingItemId, CloudData.seatPleatingOptions, { ref: false })}
						/>
						<SeatPleatingItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'EMBROIDERY'}
						shouldStartClosed={true}
						modalContent={CloudData.getModalContent(selectedEmbroideryItemId, CloudData.headrestEmbroideryOptions)}
					>
						<SelectedSectionItem
							itemName={selectedEmbroideryItemName}
							sectionPrice={CloudData.getPrice(selectedEmbroideryItemId, CloudData.headrestEmbroideryOptions, { ref: false })}
						/>
						<EmbroideryItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</SeatsViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const SeatsViewContext = createContext<SeatsViewContextType | undefined>(undefined);

function SeatPleatingItems() {

	// the quilted and box stitch styles don't apply to two-tone leather, so disable if the current seat back style is set to two-tone (which it will be if two-tone leather is selected).
	const isDisabled = Configuration.seatBackStyle === SeatBackStyles.TwoToneTextile;

	return (
		<ViewContentRows>
			<SeatPleatingItem id={'T001'} imageFilePath={'horizontal'} />
			<SeatPleatingItem id={'T002'} imageFilePath={'quilted'} isDisabled={isDisabled} />
			<SeatPleatingItem id={'T003'} imageFilePath={'box-stitch'} isDisabled={isDisabled} />
		</ViewContentRows>
	);
}

function SeatPleatingItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(SeatsViewContext);
	if (!context) {
		throw new Error('SeatsViewContext must be used within a SeatsView component');
	}

	// get the file path to the relevant image according to the currently selected leather style.
	const imageFilePath = `assets/images/configuration-items/interior/seat-pleating/${props.imageFilePath}/${getFilePathForLeatherStyleId(Configuration.leatherStyle)}.webp`;

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={imageFilePath}
			isSelected={props.id === context.selectedPleatingItemId}
			isDisabled={props.isDisabled}
			onItemClicked={context.handlePleatingItemClicked}
		/>
	);
}
function EmbroideryItems() {
	return (
		<ViewContentRows>
			<EmbroideryItem id={'HR004'} imageFilePath={'none-dynamically-generated'} />
			<EmbroideryItem id={'HR001'} imageFilePath={'wings-logo'} />
			<EmbroideryItem id={'HR003'} imageFilePath={'custom-logo'} />
		</ViewContentRows>
	);
}

function EmbroideryItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(SeatsViewContext);
	if (!context) {
		throw new Error('SeatsViewContext must be used within a SeatsView component');
	}

	// if the 'none' option is chosen, get the file path to the relevant leather image instead of using the given path.
	const leatherFilePath = `assets/images/configuration-items/interior/leather/${getFilePathForLeatherStyleId(Configuration.leatherStyle)}.webp`;

	const stitchingColour = context.interiorConfigurator.getCurrentStitchingColour();
	const hexStitchColour = Utilities.rgbaToHex(stitchingColour);

	// return the HTML.
	return (
		<div className="embroidery-item-container">
			<SectionItem
				id={props.id}
				itemName={getEmbroideryItemName(props.id)}
				imageFilePath={leatherFilePath}
				isSelected={props.id === context.selectedEmbroideryItemId}
				onItemClicked={context.handleEmbroideryItemClicked}
			/>
			{props.id !== EmbroideryOptions.None && (
				<div className="embroidery-overlay">
					{props.id === EmbroideryOptions.Wings ? (
						<WingsLogo fill={hexStitchColour} />
					) : (
						<CustomLogo fill={hexStitchColour} />
					)}
				</div>
			)}
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.seatPleatingOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getEmbroideryItemName(id: string): string {
	return CloudData.getName(id, CloudData.headrestEmbroideryOptions);
}
